import React, { useState, useEffect, useRef } from 'react';
import { COLORS } from '../../utils/constants';
import { adminAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { Link } from 'react-router-dom';
import {
  Users, DollarSign, TrendingUp, MessageSquare,
  Clock, Bell, Volume2, VolumeX, Briefcase,
  UserPlus, Activity, ChevronRight, BarChart3, AlertTriangle,
  ArrowUpCircle, ArrowDownCircle, UserCheck, Tag, Eye
} from 'lucide-react';

const AdminDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [extendedStats, setExtendedStats] = useState(null);
  const [activityData, setActivityData] = useState([]);
  const [alerts, setAlerts] = useState({ pending_withdrawals: 0, new_withdrawals_last_hour: 0 });
  const [loading, setLoading] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const prevWithdrawalsRef = useRef(0);

  const role = user?.role || 'manager';
  const isSuperAdmin = role === 'super_admin';
  const isAdmin = role === 'admin';
  const isManager = role === 'manager';

  useEffect(() => {
    loadDashboard();
    loadActivityGraph();
    loadAlerts();
    
    const interval = setInterval(() => {
      loadDashboard();
      loadAlerts();
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (soundEnabled && alerts.pending_withdrawals > prevWithdrawalsRef.current) {
      playAlertSound();
    }
    prevWithdrawalsRef.current = alerts.pending_withdrawals;
  }, [alerts.pending_withdrawals, soundEnabled]);

  const playAlertSound = () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      gainNode.gain.value = 0.3;
      oscillator.start();
      setTimeout(() => {
        oscillator.stop();
        audioContext.close();
      }, 200);
    } catch (e) {
      console.log('Audio not supported');
    }
  };

  const loadDashboard = async () => {
    try {
      const [res, extRes] = await Promise.all([
        adminAPI.getDashboard(),
        adminAPI.getExtendedDashboard()
      ]);
      setStats(res.data);
      setExtendedStats(extRes.data);
    } catch (err) {
      console.error('Failed to load dashboard:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadActivityGraph = async () => {
    try {
      const res = await adminAPI.getActivityGraph(14);
      setActivityData(res.data.data || []);
    } catch (err) {
      console.error('Failed to load activity graph:', err);
    }
  };

  const loadAlerts = async () => {
    try {
      const res = await adminAPI.getPendingAlerts();
      setAlerts(res.data);
    } catch (err) {
      console.error('Failed to load alerts:', err);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh', color: COLORS.textSecondary }}>
        Загрузка...
      </div>
    );
  }

  const statusColors = {
    NEW: '#3b82f6', IN_WORK: COLORS.primary, LOUD: '#8b5cf6',
    OPEN: COLORS.success, WITHDRAWAL: COLORS.danger, LOUD2: '#ec4899',
    RESET: '#6b7280', ACTIVE: COLORS.success, VIP: '#f0b90b'
  };

  const maxValue = Math.max(...activityData.map(d => Math.max(d.registrations, d.logins, d.trades, d.deposits)), 1);

  const greeting = extendedStats?.user_info?.name || user?.email || 'Администратор';

  // ============ RENDER BASED ON ROLE ============

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ color: COLORS.text, fontSize: '24px', fontWeight: '700', marginBottom: '4px' }}>
            {isSuperAdmin ? '🛡️ Панель Супер Админа' : isAdmin ? '👔 Панель Администратора' : '👤 Панель Менеджера'}
          </h1>
          <div style={{ color: COLORS.textSecondary, fontSize: '14px' }}>Добро пожаловать, {greeting}</div>
        </div>
        
        <button
          onClick={() => setSoundEnabled(!soundEnabled)}
          style={{
            padding: '8px 12px', backgroundColor: soundEnabled ? `${COLORS.primary}20` : COLORS.card,
            border: `1px solid ${soundEnabled ? COLORS.primary : COLORS.border}`, borderRadius: '8px',
            color: soundEnabled ? COLORS.primary : COLORS.textSecondary, cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px',
          }}
        >
          {soundEnabled ? <Volume2 size={16} /> : <VolumeX size={16} />}
          Звук
        </button>
      </div>

      {/* Urgent Alert Banner */}
      {alerts.pending_withdrawals > 0 && (
        <Link to="/admin-panel/requests" style={{ textDecoration: 'none' }}>
          <div style={{
            backgroundColor: `${COLORS.danger}15`, border: `1px solid ${COLORS.danger}40`,
            borderRadius: '12px', padding: '16px 20px', marginBottom: '20px',
            display: 'flex', alignItems: 'center', gap: '12px',
          }}>
            <AlertTriangle size={24} color={COLORS.danger} />
            <div style={{ flex: 1 }}>
              <div style={{ color: COLORS.danger, fontWeight: '600', fontSize: '15px' }}>
                Требуют внимания: {alerts.pending_withdrawals} заявок на вывод
              </div>
              {alerts.new_withdrawals_last_hour > 0 && (
                <div style={{ color: COLORS.textSecondary, fontSize: '13px', marginTop: '2px' }}>
                  +{alerts.new_withdrawals_last_hour} новых за последний час
                </div>
              )}
            </div>
            <ChevronRight size={20} color={COLORS.danger} />
          </div>
        </Link>
      )}

      {/* ============ SUPER ADMIN DASHBOARD ============ */}
      {isSuperAdmin && extendedStats && (
        <>
          {/* Team Overview */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px', marginBottom: '20px' }}>
            <StatCard icon={Briefcase} label="Команда" value={extendedStats.team_stats?.total_team || 0} color="#8b5cf6" subtext={`${extendedStats.team_stats?.total_admins || 0} админов`} />
            <StatCard icon={Users} label="Всего клиентов" value={extendedStats.client_stats?.total || 0} color="#3b82f6" link="/admin-panel/clients" />
            <StatCard icon={UserPlus} label="Новые сегодня" value={extendedStats.client_stats?.new_today || 0} color={COLORS.success} />
            <StatCard icon={UserPlus} label="За неделю" value={extendedStats.client_stats?.new_week || 0} color="#06b6d4" />
            <StatCard icon={UserPlus} label="За месяц" value={extendedStats.client_stats?.new_month || 0} color="#8b5cf6" />
            <StatCard icon={Tag} label="Нужно назначить" value={extendedStats.client_stats?.need_assignment || 0} color={COLORS.danger} urgent />
          </div>

          {/* Financial Overview */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '20px' }}>
            <StatCard icon={DollarSign} label="Общий баланс" value={`$${(extendedStats.financial_stats?.total_balance || 0).toFixed(0)}`} color={COLORS.primary} />
            <StatCard icon={ArrowUpCircle} label="Всего депозитов" value={`$${(extendedStats.financial_stats?.total_deposits || 0).toFixed(0)}`} color={COLORS.success} />
            <StatCard icon={ArrowDownCircle} label="Всего выводов" value={`$${(extendedStats.financial_stats?.total_withdrawals || 0).toFixed(0)}`} color={COLORS.danger} />
            <StatCard icon={TrendingUp} label="Чистый доход" value={`$${(extendedStats.financial_stats?.net_income || 0).toFixed(0)}`} color={extendedStats.financial_stats?.net_income >= 0 ? COLORS.success : COLORS.danger} />
            <StatCard icon={Clock} label="Депозиты (месяц)" value={`$${(extendedStats.financial_stats?.deposits_this_month || 0).toFixed(0)}`} color="#06b6d4" />
          </div>

          {/* Pending Requests */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginBottom: '20px' }}>
            <Link to="/admin-panel/requests" style={{ textDecoration: 'none' }}>
              <div style={{ backgroundColor: COLORS.card, borderRadius: '12px', padding: '20px', border: `1px solid ${COLORS.border}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ color: COLORS.textSecondary, fontSize: '13px' }}>Ожидают проверки</div>
                    <div style={{ color: COLORS.text, fontSize: '32px', fontWeight: '700' }}>{extendedStats.pending?.total || 0}</div>
                  </div>
                  <div>
                    <div style={{ color: COLORS.success, fontSize: '14px' }}>💰 {extendedStats.pending?.deposits || 0} депозитов</div>
                    <div style={{ color: COLORS.danger, fontSize: '14px' }}>💸 {extendedStats.pending?.withdrawals || 0} выводов</div>
                  </div>
                </div>
              </div>
            </Link>
            <Link to="/admin-panel/positions" style={{ textDecoration: 'none' }}>
              <div style={{ backgroundColor: COLORS.card, borderRadius: '12px', padding: '20px', border: `1px solid ${COLORS.border}` }}>
                <div style={{ color: COLORS.textSecondary, fontSize: '13px' }}>Открытых позиций</div>
                <div style={{ color: COLORS.primary, fontSize: '32px', fontWeight: '700' }}>{extendedStats.trading?.open_positions || 0}</div>
              </div>
            </Link>
          </div>

          {/* Top Staff */}
          {extendedStats.top_staff && extendedStats.top_staff.length > 0 && (
            <div style={{ backgroundColor: COLORS.card, borderRadius: '12px', padding: '20px', border: `1px solid ${COLORS.border}`, marginBottom: '20px' }}>
              <h3 style={{ color: COLORS.text, fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>🏆 Топ сотрудников по клиентам</h3>
              <div style={{ display: 'grid', gap: '8px' }}>
                {extendedStats.top_staff.map((staff, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px', backgroundColor: COLORS.background, borderRadius: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ color: COLORS.primary, fontWeight: '700', width: '24px' }}>#{i + 1}</span>
                      <span style={{ color: COLORS.text }}>{staff.name}</span>
                      <span style={{ fontSize: '11px', padding: '2px 6px', backgroundColor: staff.role === 'admin' ? '#8b5cf620' : '#3b82f620', color: staff.role === 'admin' ? '#8b5cf6' : '#3b82f6', borderRadius: '4px' }}>
                        {staff.role === 'admin' ? 'Админ' : 'Менеджер'}
                      </span>
                    </div>
                    <span style={{ color: COLORS.text, fontWeight: '600' }}>{staff.count} клиентов</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* ============ ADMIN DASHBOARD ============ */}
      {isAdmin && extendedStats && (
        <>
          {/* Team & Client Overview */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px', marginBottom: '20px' }}>
            <StatCard icon={Users} label="Мои менеджеры" value={extendedStats.team_stats?.total_managers || 0} color="#3b82f6" link="/admin-panel/team" />
            <StatCard icon={Users} label="Всего клиентов" value={extendedStats.client_stats?.total || 0} color={COLORS.text} link="/admin-panel/clients" />
            <StatCard icon={UserCheck} label="Напрямую на мне" value={extendedStats.client_stats?.direct || 0} color="#8b5cf6" />
            <StatCard icon={Users} label="Через менеджеров" value={extendedStats.client_stats?.via_managers || 0} color="#06b6d4" />
            <StatCard icon={UserPlus} label="Новые сегодня" value={extendedStats.client_stats?.new_today || 0} color={COLORS.success} />
          </div>

          {/* Financial */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '20px' }}>
            <StatCard icon={DollarSign} label="Баланс команды" value={`$${(extendedStats.financial_stats?.total_balance || 0).toFixed(0)}`} color={COLORS.primary} />
            <StatCard icon={ArrowUpCircle} label="Депозиты" value={`$${(extendedStats.financial_stats?.total_deposits || 0).toFixed(0)}`} color={COLORS.success} />
            <StatCard icon={ArrowDownCircle} label="Выводы" value={`$${(extendedStats.financial_stats?.total_withdrawals || 0).toFixed(0)}`} color={COLORS.danger} />
            <StatCard icon={TrendingUp} label="Чистый доход" value={`$${(extendedStats.financial_stats?.net_income || 0).toFixed(0)}`} color={extendedStats.financial_stats?.net_income >= 0 ? COLORS.success : COLORS.danger} />
          </div>

          {/* Pending & Positions */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginBottom: '20px' }}>
            <Link to="/admin-panel/requests" style={{ textDecoration: 'none' }}>
              <div style={{ backgroundColor: COLORS.card, borderRadius: '12px', padding: '20px', border: `1px solid ${COLORS.border}` }}>
                <div style={{ color: COLORS.textSecondary, fontSize: '13px' }}>Ожидают проверки</div>
                <div style={{ display: 'flex', gap: '20px', marginTop: '8px' }}>
                  <div><span style={{ color: COLORS.success, fontSize: '24px', fontWeight: '700' }}>{extendedStats.pending?.deposits || 0}</span> <span style={{ color: COLORS.textSecondary, fontSize: '12px' }}>депозитов</span></div>
                  <div><span style={{ color: COLORS.danger, fontSize: '24px', fontWeight: '700' }}>{extendedStats.pending?.withdrawals || 0}</span> <span style={{ color: COLORS.textSecondary, fontSize: '12px' }}>выводов</span></div>
                </div>
              </div>
            </Link>
            <Link to="/admin-panel/positions" style={{ textDecoration: 'none' }}>
              <div style={{ backgroundColor: COLORS.card, borderRadius: '12px', padding: '20px', border: `1px solid ${COLORS.border}` }}>
                <div style={{ color: COLORS.textSecondary, fontSize: '13px' }}>Открытых позиций</div>
                <div style={{ color: COLORS.primary, fontSize: '28px', fontWeight: '700' }}>{extendedStats.trading?.open_positions || 0}</div>
              </div>
            </Link>
          </div>

          {/* My Managers Performance */}
          {extendedStats.team_stats?.managers && extendedStats.team_stats.managers.length > 0 && (
            <div style={{ backgroundColor: COLORS.card, borderRadius: '12px', padding: '20px', border: `1px solid ${COLORS.border}`, marginBottom: '20px' }}>
              <h3 style={{ color: COLORS.text, fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>👥 Мои менеджеры</h3>
              <div style={{ display: 'grid', gap: '8px' }}>
                {extendedStats.team_stats.managers.map((mgr, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px', backgroundColor: COLORS.background, borderRadius: '8px' }}>
                    <span style={{ color: COLORS.text }}>{mgr.name}</span>
                    <span style={{ color: COLORS.primary, fontWeight: '600' }}>{mgr.client_count} клиентов</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Status Breakdown */}
          {extendedStats.client_stats?.by_status && Object.keys(extendedStats.client_stats.by_status).length > 0 && (
            <div style={{ backgroundColor: COLORS.card, borderRadius: '12px', padding: '20px', border: `1px solid ${COLORS.border}`, marginBottom: '20px' }}>
              <h3 style={{ color: COLORS.text, fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>📊 Клиенты по статусам</h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                {Object.entries(extendedStats.client_stats.by_status).map(([status, count]) => (
                  <div key={status} style={{ padding: '12px 16px', backgroundColor: `${statusColors[status] || '#6b7280'}15`, borderRadius: '8px', border: `1px solid ${statusColors[status] || '#6b7280'}30` }}>
                    <div style={{ color: statusColors[status] || '#6b7280', fontSize: '24px', fontWeight: '700' }}>{count}</div>
                    <div style={{ color: COLORS.text, fontSize: '12px' }}>{status}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* ============ MANAGER DASHBOARD ============ */}
      {isManager && extendedStats && (
        <>
          {/* Client Overview */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px', marginBottom: '20px' }}>
            <StatCard icon={Users} label="Мои клиенты" value={extendedStats.client_stats?.total || 0} color="#3b82f6" link="/admin-panel/clients" />
            <StatCard icon={Activity} label="Активны за неделю" value={extendedStats.client_stats?.active_week || 0} color={COLORS.success} />
            <StatCard icon={DollarSign} label="Общий баланс" value={`$${(extendedStats.financial_stats?.total_balance || 0).toFixed(0)}`} color={COLORS.primary} />
          </div>

          {/* Financial */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '20px' }}>
            <StatCard icon={ArrowUpCircle} label="Депозиты" value={`$${(extendedStats.financial_stats?.total_deposits || 0).toFixed(0)}`} color={COLORS.success} />
            <StatCard icon={ArrowDownCircle} label="Выводы" value={`$${(extendedStats.financial_stats?.total_withdrawals || 0).toFixed(0)}`} color={COLORS.danger} />
            <StatCard icon={TrendingUp} label="Открытых позиций" value={extendedStats.trading?.open_positions || 0} color={COLORS.primary} link="/admin-panel/positions" />
          </div>

          {/* Pending */}
          <Link to="/admin-panel/requests" style={{ textDecoration: 'none' }}>
            <div style={{ backgroundColor: COLORS.card, borderRadius: '12px', padding: '20px', border: `1px solid ${COLORS.border}`, marginBottom: '20px' }}>
              <div style={{ color: COLORS.textSecondary, fontSize: '13px', marginBottom: '8px' }}>Ожидают проверки</div>
              <div style={{ display: 'flex', gap: '24px' }}>
                <div><span style={{ color: COLORS.success, fontSize: '28px', fontWeight: '700' }}>{extendedStats.pending?.deposits || 0}</span> <span style={{ color: COLORS.textSecondary }}>депозитов</span></div>
                <div><span style={{ color: COLORS.danger, fontSize: '28px', fontWeight: '700' }}>{extendedStats.pending?.withdrawals || 0}</span> <span style={{ color: COLORS.textSecondary }}>выводов</span></div>
              </div>
            </div>
          </Link>

          {/* Status Breakdown */}
          {extendedStats.client_stats?.by_status && Object.keys(extendedStats.client_stats.by_status).length > 0 && (
            <div style={{ backgroundColor: COLORS.card, borderRadius: '12px', padding: '20px', border: `1px solid ${COLORS.border}`, marginBottom: '20px' }}>
              <h3 style={{ color: COLORS.text, fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>📊 Мои клиенты по статусам</h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                {Object.entries(extendedStats.client_stats.by_status).map(([status, count]) => (
                  <div key={status} style={{ padding: '12px 16px', backgroundColor: `${statusColors[status] || '#6b7280'}15`, borderRadius: '8px', border: `1px solid ${statusColors[status] || '#6b7280'}30` }}>
                    <div style={{ color: statusColors[status] || '#6b7280', fontSize: '24px', fontWeight: '700' }}>{count}</div>
                    <div style={{ color: COLORS.text, fontSize: '12px' }}>{status}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Top Clients */}
          {extendedStats.top_clients && extendedStats.top_clients.length > 0 && (
            <div style={{ backgroundColor: COLORS.card, borderRadius: '12px', padding: '20px', border: `1px solid ${COLORS.border}`, marginBottom: '20px' }}>
              <h3 style={{ color: COLORS.text, fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>💎 Топ клиентов по балансу</h3>
              <div style={{ display: 'grid', gap: '8px' }}>
                {extendedStats.top_clients.map((client, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px', backgroundColor: COLORS.background, borderRadius: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ color: COLORS.primary, fontWeight: '700', width: '24px' }}>#{i + 1}</span>
                      <div>
                        <div style={{ color: COLORS.text }}>{client.name}</div>
                        <div style={{ color: COLORS.textSecondary, fontSize: '11px' }}>{client.email}</div>
                      </div>
                      {client.status && (
                        <span style={{ fontSize: '10px', padding: '2px 6px', backgroundColor: `${statusColors[client.status] || '#6b7280'}20`, color: statusColors[client.status] || '#6b7280', borderRadius: '4px' }}>
                          {client.status}
                        </span>
                      )}
                    </div>
                    <span style={{ color: COLORS.success, fontWeight: '600' }}>${client.balance?.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* ============ ACTIVITY GRAPH (for all roles) ============ */}
      {activityData.length > 0 && (
        <div style={{ backgroundColor: COLORS.card, borderRadius: '12px', padding: '20px', border: `1px solid ${COLORS.border}`, marginBottom: '24px' }}>
          <h3 style={{ color: COLORS.text, fontSize: '16px', fontWeight: '600', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <BarChart3 size={18} color={COLORS.primary} />
            Активность за 14 дней
          </h3>
          
          <div style={{ display: 'flex', gap: '16px', marginBottom: '16px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><div style={{ width: '12px', height: '12px', borderRadius: '2px', backgroundColor: '#3b82f6' }} /><span style={{ color: COLORS.textSecondary, fontSize: '12px' }}>Регистрации</span></div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><div style={{ width: '12px', height: '12px', borderRadius: '2px', backgroundColor: COLORS.success }} /><span style={{ color: COLORS.textSecondary, fontSize: '12px' }}>Входы</span></div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><div style={{ width: '12px', height: '12px', borderRadius: '2px', backgroundColor: COLORS.primary }} /><span style={{ color: COLORS.textSecondary, fontSize: '12px' }}>Сделки</span></div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><div style={{ width: '12px', height: '12px', borderRadius: '2px', backgroundColor: '#8b5cf6' }} /><span style={{ color: COLORS.textSecondary, fontSize: '12px' }}>Депозиты</span></div>
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px', height: '140px', overflowX: 'auto' }}>
            {activityData.map((day, i) => (
              <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: '50px' }}>
                <div style={{ display: 'flex', gap: '2px', alignItems: 'flex-end', height: '100px' }}>
                  <div style={{ width: '8px', height: `${(day.registrations / maxValue) * 100}%`, backgroundColor: '#3b82f6', borderRadius: '2px 2px 0 0', minHeight: '2px' }} />
                  <div style={{ width: '8px', height: `${(day.logins / maxValue) * 100}%`, backgroundColor: COLORS.success, borderRadius: '2px 2px 0 0', minHeight: '2px' }} />
                  <div style={{ width: '8px', height: `${(day.trades / maxValue) * 100}%`, backgroundColor: COLORS.primary, borderRadius: '2px 2px 0 0', minHeight: '2px' }} />
                  <div style={{ width: '8px', height: `${(day.deposits / maxValue) * 100}%`, backgroundColor: '#8b5cf6', borderRadius: '2px 2px 0 0', minHeight: '2px' }} />
                </div>
                <div style={{ color: COLORS.textSecondary, fontSize: '10px', marginTop: '6px' }}>{day.date.slice(5)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Stat Card Component
const StatCard = ({ icon: Icon, label, value, color, link, subtext, urgent }) => {
  const content = (
    <div style={{
      backgroundColor: urgent ? `${color}15` : COLORS.card,
      borderRadius: '12px', padding: '16px',
      border: `1px solid ${urgent ? color : COLORS.border}`,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
        <div style={{ width: '36px', height: '36px', borderRadius: '8px', backgroundColor: `${color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon size={18} color={color} />
        </div>
        {link && <ChevronRight size={16} color={COLORS.textSecondary} />}
      </div>
      <div style={{ color: COLORS.textSecondary, fontSize: '12px', marginBottom: '2px' }}>{label}</div>
      <div style={{ color, fontSize: '22px', fontWeight: '700' }}>{value}</div>
      {subtext && <div style={{ color: COLORS.textSecondary, fontSize: '11px' }}>{subtext}</div>}
    </div>
  );
  
  return link ? <Link to={link} style={{ textDecoration: 'none' }}>{content}</Link> : content;
};

export default AdminDashboard;
